import React, {Suspense, useEffect, useState} from 'react';
import {Routes, Route, Navigate, useLocation, matchPath} from "react-router-dom";
import {useAppDispatch, useAppSelector} from "../hooks/redux";
import {fetchCheckAuth} from "../features/auth/model/actions";
import {fetchGetAllUsers} from "../features/users/model/actions";
import {CssBaseline, Stack, ThemeProvider, Box as MuiBox, useMediaQuery} from "@mui/material";
import Box from "@mui/material/Box";
import {IRouteConfig, routesConfig} from "../config/routes";
import SideMenu from "./layout/sideBar/SideMenu";
import AppNavbar from "./layout/AppNavbar";
import {theme} from "../styles/theme/theme";
import MessageWindow from "../features/messages/ui/MessageWindow";
import Message from "../features/messages/ui/Message";
import {selectIsAuth} from "../features/auth/model/selectors";
import CircularProgress from "@mui/material/CircularProgress";
import {routes} from "../utils/routes";
import {selectCurrentUser} from "../features/users/model/selectors";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import {LocalizationProvider} from "@mui/x-date-pickers";
import {ruRU} from "@mui/x-date-pickers/locales";
import {MobileHeaderActionsProvider} from "../context/MobileHeaderActionsContext";
import {t} from '../styles/theme/tokens';
import {MOBILE_NAVBAR_HEIGHT} from "./layout/layout.constants";
import {useIsAdmin} from "../hooks/useIsAdmin"; // ← путь поправь под свой
import ErrorBoundary from "./common/ErrorBoundary";
import {useNotifications} from "../hooks/useNotifications";
import AppRealtime from "./common/AppRealtime";
import {usePermissions} from "../hooks/usePermissions";
import {canRoute} from "../models/permissions";

const PageFallback = () => (
    <MuiBox sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1}}>
        <CircularProgress/>
    </MuiBox>
);

function App() {
    const dispatch = useAppDispatch();
    const [isCheckingAuth, setIsCheckingAuth] = useState(true);
    const isAuth = useAppSelector(selectIsAuth);
    const isAdmin = useIsAdmin();
    // Ролевой гард роутов (requiresRoles): текущий пользователь из стора.
    const currentUser = useAppSelector(selectCurrentUser);                                    // ← роль для гарда
    const isNavbarVisible = useMediaQuery(theme.breakpoints.down("md"));
    const isPhone = useMediaQuery(theme.breakpoints.down("sm"));
    const location = useLocation();
    const isMapRoute = location.pathname.startsWith('/map');
    // Доске нужна вся ширина: боковое меню свёрнуто (открывается из профиля
    // в шапке доски), мобильный навбар не показывается — у доски своя шапка.
    const isBoardRoute = matchPath(routes.machines, location.pathname) !== null;
    const isFullBleed = isMapRoute || isBoardRoute;
    // Ограниченные роли (ROLES.md § 4): меню и навбар — по `menu`; маршрут не из `routes` — на доску.
    const can = usePermissions();

    useEffect(() => {
        if (!localStorage.getItem("access_token")) {
            setIsCheckingAuth(false);
            return;
        }
        dispatch(fetchCheckAuth()).finally(() => setIsCheckingAuth(false));
    }, [dispatch]);

    useEffect(() => {
        // Список сотрудников нужен всем, в т. ч. «только доске» (S12): имена в подсказках реакций.
        if (isAuth) dispatch(fetchGetAllUsers());
    }, [isAuth, dispatch]);

    // Оповещения в приложении: подключение к сокету после входа.
    useNotifications();

    const getAllRoutes = (routesList: IRouteConfig[]): IRouteConfig[] => {
        const result: IRouteConfig[] = [];
        routesList.forEach(route => {
            result.push(route);
            if (route.children) result.push(...getAllRoutes(route.children));
        });
        return result;
    };

    const allRoutes = getAllRoutes(routesConfig);
    // standalone исключены из обеих обычных веток: они рендерятся отдельно
    // ниже и не должны попадать внутрь оболочки приложения.
    const standaloneRoutes = allRoutes.filter(r => r.standalone);
    const publicRoutes = allRoutes.filter(r => !r.requiresAuth && !r.standalone);
    const privateRoutes = allRoutes.filter(r => r.requiresAuth);

    /**
     * Публичные страницы по токену (гостевая группа, отслеживание рейса)
     * рендерятся ДО app-shell и ДО проверки авторизации.
     *
     * Почему до, а не «просто в публичной ветке»: publicRoutes раньше
     * подмешивались И в авторизованную ветку тоже. У посетителя с живым
     * access_token в этой вкладке (владелец, открывший свою же ссылку, или
     * защёлкнутая демо-сессия с лендинга) гостевая страница монтировалась
     * внутрь SideMenu/AppNavbar — и гость получал навигацию по чужому
     * кабинету. В dev этого не воспроизводилось: там вкладка была чистая,
     * isAuth === false, и отрабатывала публичная ветка.
     *
     * Ранний выход делает утечку невозможной структурно: до оболочки
     * исполнение просто не доходит. Ждать isCheckingAuth тоже незачем —
     * гостю авторизация не нужна, а лишний спиннер задерживал бы страницу.
     */
    const isStandaloneRoute = standaloneRoutes.some(
        (r) => matchPath(r.path, location.pathname) !== null,
    );

    if (isStandaloneRoute) {
        return (
            <ThemeProvider theme={theme} defaultMode="light">
                <LocalizationProvider
                    dateAdapter={AdapterDayjs}
                    adapterLocale="ru"
                    localeText={ruRU.components.MuiLocalizationProvider.defaultProps.localeText}
                >
                    <CssBaseline/>
                    <Suspense fallback={<PageFallback/>}>
                        <Routes>
                            {standaloneRoutes.map(route => (
                                <Route key={route.path} path={route.path} element={route.element}/>
                            ))}
                        </Routes>
                    </Suspense>
                </LocalizationProvider>
            </ThemeProvider>
        );
    }

    if (isCheckingAuth) {
        return (
            <ThemeProvider theme={theme} defaultMode="light">
                <CssBaseline/>
                <MuiBox sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100dvh'}}>
                    <CircularProgress/>
                </MuiBox>
            </ThemeProvider>
        );
    }

    if (!isAuth) {
        return (
            <ThemeProvider theme={theme} defaultMode="light">
                {/* LocalizationProvider нужен и здесь: публичные страницы тоже
                    показывают MyDatePicker (гостевой отчёт по группе), а без
                    контекста пикер падает на этапе рендера. Раньше провайдер
                    стоял только в авторизованной ветке. */}
                <LocalizationProvider
                    dateAdapter={AdapterDayjs}
                    adapterLocale="ru"
                    localeText={ruRU.components.MuiLocalizationProvider.defaultProps.localeText}
                >
                    <CssBaseline/>
                    <Suspense fallback={<PageFallback/>}>
                        <Routes>
                            {publicRoutes.map(route => (
                                <Route key={route.path} path={route.path} element={route.element}/>
                            ))}
                            {/* Гость на любой неизвестный/приватный путь → форма входа; после входа — обратно (ссылка на машину, S15) */}
                            <Route path="*" element={<Navigate to={routes.login} state={{from: location}}/>}/>
                        </Routes>
                    </Suspense>
                    <MessageWindow/>
                    <Message/>
                </LocalizationProvider>
            </ThemeProvider>
        );
    }

    return (
        <ThemeProvider theme={theme} defaultMode="light">
            <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale="ru"
                localeText={ruRU.components.MuiLocalizationProvider.defaultProps.localeText}
            >
                <MobileHeaderActionsProvider>
                    <CssBaseline/>
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        height: '100dvh',
                        overflow: 'hidden',
                    }}>
                        {!isBoardRoute && can.menu && <SideMenu/>}
                        {!isBoardRoute && can.menu && <AppNavbar/>}
                        <Box
                            component="main"
                            sx={{
                                flexGrow: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                overflow: 'hidden',
                                backgroundColor: t.bg.default,
                            }}
                        >
                            <Stack
                                sx={{
                                    flexGrow: 1,
                                    minHeight: 0,
                                    height: isFullBleed ? '100%' : 'auto',
                                    overflow: isFullBleed ? 'hidden' : 'auto',
                                    px: isBoardRoute || (isPhone && isFullBleed) ? 0 : (isPhone ? 1 : 2),
                                    pb: isBoardRoute || (isPhone && isFullBleed) ? 0 : (isPhone ? 1 : 2),
                                    pt: (isNavbarVisible && !isFullBleed) ? `${MOBILE_NAVBAR_HEIGHT + 16}px` : 0,
                                    boxSizing: 'border-box',
                                    scrollbarGutter: isFullBleed ? 'auto' : 'stable',
                                }}
                                spacing={0}
                            >
                                {/* Страничный ErrorBoundary: падение контента страницы
                                    не сносит layout (SideMenu/AppNavbar остаются живыми).
                                    key={pathname} пересоздаёт бордер при смене роута, чтобы
                                    навигация по меню сбрасывала hasError и восстанавливала
                                    контент без полного reload. */}
                                <ErrorBoundary key={location.pathname}>
                                    <Suspense fallback={<PageFallback/>}>
                                        {can.routes !== "all" ? (
                                            <Routes>
                                                {privateRoutes.filter(route => canRoute(can, route.path)).map(route => (
                                                    <Route key={route.path} path={route.path} element={route.element}/>
                                                ))}
                                                <Route path="*" element={<Navigate to={routes.machines} replace/>}/>
                                            </Routes>
                                        ) : (
                                        <Routes>
                                            {privateRoutes.map(route => {
                                                // Гард по роли админа.
                                                const deniedByAdmin =
                                                    route.requiresAdmin && !isAdmin;
                                                // Гард по роли: список requiresRoles задан и
                                                // роль текущего пользователя в него не входит.
                                                const deniedByRole =
                                                    route.requiresRoles &&
                                                    !route.requiresRoles.includes(currentUser?.role_id ?? -1);

                                                return (
                                                    <Route
                                                        key={route.path}
                                                        path={route.path}
                                                        element={
                                                            (deniedByAdmin || deniedByRole)
                                                                ? <Navigate to={routes.machines} replace/>
                                                                : route.element
                                                        }
                                                    />
                                                );
                                            })}
                                            {publicRoutes.map(route => (
                                                <Route key={route.path} path={route.path} element={route.element}/>
                                            ))}
                                            {/* Стартовая для авторизованного: "/" → машины.
                                            Гостя это не затрагивает — у него отдельная ветка выше. */}
                                            <Route path="/" element={<Navigate to={routes.machines} replace/>}/>
                                            {/* Авторизованный на неизвестный путь → машины (дом) */}
                                            <Route path="*" element={<Navigate to={routes.machines}/>}/>
                                        </Routes>
                                        )}
                                    </Suspense>
                                </ErrorBoundary>
                            </Stack>
                        </Box>
                        <MessageWindow/>
                        <Message/>
                        {/* Сокеты доски/чата/эмодзи и заголовок вкладки — на всех страницах (S4.1). */}
                        <AppRealtime/>
                    </Box>
                </MobileHeaderActionsProvider>
            </LocalizationProvider>
        </ThemeProvider>
    );
}

export default App;